import { create } from 'zustand';
import type { AssessmentMode, ChatMessage, OrgSummary, PracticeCompletion, PracticeScore, ReportView, RctoFields, SessionSummary, TemplateView, UserTemplateView } from './types';
import { sampleAveryReplies, sampleHistory, sampleOpening, samplePractice, sampleTemplates, sampleApprovedTools } from './sample';
import { api, isSampleMode } from './api/client';
import { AveryWs, wsEnabled } from './api/ws';
import { authConfigured, isAuthed, currentUser, currentRole, login } from './auth/cognito';
import { STEPS, INITIAL_SKILLS, renderAnswer, mergeTailored, type Skills, type Step } from './assessment-steps';

/** Live mode = a backend is configured and the user is signed in. */
const liveMode = () => !isSampleMode && isAuthed();

/**
 * The assessment mode the UI assumes before the backend assigns one. In live mode the
 * authoritative mode comes from the session (set at create); this build-time flag only drives
 * sample mode and the pre-create intro copy. Defaults to `quick` (off-by-default rollout).
 */
const prospectiveMode = (): AssessmentMode =>
  import.meta.env.VITE_ASSESSMENT_DEFAULT_MODE === 'conversational' ? 'conversational' : 'quick';

/** Per-session defaults for the interactive (6-step) live assessment. */
const freshInteractive = () => ({
  stepIdx: 0,
  revealed: false,
  insightsCount: 0,
  skills: { ...INITIAL_SKILLS } as Skills,
  choice: null as string | null,
  promptSel: {} as Record<string, boolean>,
  rankOrder: null as string[] | null,
  sliderVal: 50,
  openText: '',
  answers: [] as string[],
  tailoredSteps: null as Step[] | null,
});

export type Screen = 'welcome' | 'assessment' | 'report' | 'practice' | 'templates' | 'organisations' | 'orgDetail' | 'history' | 'settings' | 'setup';

const EMPTY_RCTO: RctoFields = { role: '', context: '', task: '', outputFormat: '' };
export type AsmtStage = 'consent' | 'intro' | 'role' | 'setup' | 'live';

/** The built-in practice drill scenario; the Transfer step swaps in the user's own task. */
export const DEFAULT_SCENARIO =
  'A prospect has asked for a one-page summary of how our platform handles data residency. Draft a prompt that would get you a strong first version.';

/** Fallback drill sequence when there's no report (sample mode / direct entry). Mirrors the
 * report's practiceDrills shape so the Practice surface can run a real 3-drill sequence. */
export const BUILTIN_DRILLS: { title: string; rubricLine: string }[] = [
  { title: 'Prompt structure', rubricLine: 'Role, context, task and output format all present.' },
  { title: 'Verification habit', rubricLine: 'You can name how you checked the output.' },
  { title: 'Workflow handoff', rubricLine: 'A colleague could run it from your notes.' },
];

let msgSeq = 0;
const nextId = () => `m${++msgSeq}`;

/** Default APP-5 collection notice shown in the consent gate until a deployment sets its own. */
export const DEFAULT_NOTICE =
  'This assessment collects your responses to produce a personal AI-fluency report. Your work email, role and team may be used to tailor and group results. Data is encrypted, stored in Australia, and retained for the programme period. Anonymised, role-level trends are shared with programme administrators; your individual responses and band are not. You can export or delete your data at any time.';

// ---- Web Speech API (voice-to-text) -------------------------------------------------
type SpeechResult = { isFinal: boolean; 0: { transcript: string } };
type SpeechEvent = { resultIndex: number; results: { length: number } & Record<number, SpeechResult> };
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechEvent) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}
function speechCtor(): (new () => SpeechRecognitionLike) | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}
let recognition: SpeechRecognitionLike | null = null;

/** Retire callbacks before aborting: browsers can deliver results while shutting down. */
function discardDictation(): void {
  const rec = recognition;
  recognition = null;
  if (!rec) return;
  rec.onresult = null;
  rec.onerror = null;
  rec.onend = null;
  rec.abort();
}

interface State {
  screen: Screen;
  // assessment
  asmtStage: AsmtStage;
  mode: AssessmentMode; // quick 6-step walkthrough vs conversational Avery interview
  consentChecked: boolean;
  messages: ChatMessage[];
  composer: string;
  typing: boolean;
  listening: boolean;
  voiceError: string | null; // mic unsupported / permission denied
  topicsDone: number;
  completed: boolean; // assessment finished — lock the composer so a late answer can't 409
  // interactive assessment (the 6-step live walkthrough that replaced the chat)
  stepIdx: number;
  revealed: boolean; // current step answered
  insightsCount: number;
  skills: Skills;
  choice: string | null; // scenario option id
  promptSel: Record<string, boolean>; // prompt-builder clause toggles
  rankOrder: string[] | null; // null = use the step's initial order
  sliderVal: number; // trust dial 0–100
  openText: string;
  answers: string[]; // committed answers, rendered as text for finalize
  role: string | null; // participant role (IdP claim or picked) — tailors the steps
  tailoredSteps: Step[] | null; // Avery's role-tailored steps; null = default (presales) set
  // practice
  drillStep: number; // 0–4
  drillIndex: number; // which drill in the sequence is active (0-based)
  passedDrills: number[]; // indices of drills scored 8+ this session (for the "passed" badge)
  draft: string;
  drillScore: number | null;
  masteryDone: number;
  scenario: string; // the active drill scenario (default drill, or the user's own task)
  practiceFeedback: PracticeScore | null; // coach result for the current drill
  completions: PracticeCompletion[]; // practice drills / 48h actions the user has ticked off
  practiceScoring: boolean;
  practiceError: string | null;
  // report
  drawer: string | null; // criterion key | null
  revealCohort: boolean;
  copied: boolean;
  // templates (RCTO library + my saved templates)
  libraryTemplates: TemplateView[];
  userTemplates: UserTemplateView[];
  approvedTools: string[];
  templatesRoleLabel: string;
  templatesLoading: boolean;
  templatesError: string | null;
  showAllRoles: boolean; // false = role-specific only; true = also show the 'general' set
  builderOpen: boolean;
  builderTitle: string;
  builderFields: RctoFields;
  // admin / organisations
  suppressed: boolean; // k-anonymity toggle (true = small teams hidden)
  identified: boolean; // reveal real names in the roster (admin-only, audit-logged)
  selectedOrg: string | null; // campaignId of the org whose detail screen is open
  // campaign create/edit modal + locally-created orgs (sample-mode persistence)
  campaignModal: CampaignDraft | null;
  campaignSaving: boolean;
  campaignError: string | null;
  extraOrgs: OrgSummary[]; // orgs created this session (merged into the list by useOrganisations)
  // branding (from GET /platform/settings; falls back to the built-in FluentAI brand)
  productName: string;
  /** participant data-collection notice (APP 5) shown in the consent gate. */
  noticeCopy: string;
  /** the notice/branding version a participant consents to (recorded server-side at create). */
  consentVersion: number;
  // auth / live
  needsAuth: boolean; // backend configured but not signed in
  userName?: string;
  assessmentId: string | null;
  liveError: string | null;
  report: ReportView | null; // live report once an assessment completes
  welcomeLoading: boolean; // Dashboard report fetch in flight on boot (distinguishes "loading" from "no report yet")
  showSample: boolean; // user explicitly asked to preview the sample report (works in live mode too)
  cohort: Record<string, string> | null; // per-criterion cohort bands (k-anon; null if unavailable)
  ws: AveryWs | null; // open WebSocket when streaming is enabled
  resumable: SessionSummary | null; // an in-progress session found on boot, offered to continue
  history: SessionSummary[] | null; // the signed-in user's assessments (My assessments screen)
  historyLoading: boolean;

  // actions
  setScreen: (s: Screen) => void;
  /** Preview the built-in sample report on demand — shows the fixture even in live mode. */
  viewSample: () => void;
  /** Load the participant-facing platform settings (branding + flags) and apply branding. */
  loadPublicSettings: () => void;
  setAsmtStage: (s: AsmtStage) => void;
  setConsent: (v: boolean) => void;
  signIn: () => void;
  /** Recompute identity (userName/needsAuth) from the current token — call after auth resolves. */
  syncAuth: () => void;
  openAssessment: () => void;
  beginAssessment: () => void; // role gate → setup (asks for a role if the IdP didn't supply one)
  setRole: (role: string) => void;
  beginSetup: () => void;
  checkResumable: () => void;
  loadWelcome: () => void;
  resumeAssessment: (id: string) => void;
  dismissResumable: () => void;
  archiveAssessment: (andRestart: boolean, id?: string) => void;
  loadHistory: () => void;
  openReport: (id: string) => void;
  streamTurn: (text: string) => void;
  openConversation: () => void; // conversational mode: fire the empty-text opening turn (Avery greets)
  setComposer: (t: string) => void;
  sendMessage: (text?: string) => void;
  // interactive assessment actions
  selectChoice: (id: string) => void;
  lockIn: () => void;
  toggleClause: (key: string) => void;
  moveRank: (idx: number, dir: -1 | 1) => void;
  setSlider: (v: number) => void;
  setOpenText: (t: string) => void;
  nextStep: () => void;
  quickCommand: (cmd: string) => void;
  toggleListening: () => void;
  resolveAssumption: (id: string, resolution: 'confirmed' | 'corrected') => void;
  // practice
  setDrillStep: (n: number) => void;
  setDraft: (t: string) => void;
  submitDraft: () => void;
  startDrill: (scenario: string, gotoDraft?: boolean) => void;
  nextDrill: () => void; // advance to the next drill in the sequence (clamped at the last)
  loadCompletions: () => void; // fetch the user's saved practice completions (live mode)
  markComplete: (itemType: PracticeCompletion['itemType'], itemRef: string, reflection?: string) => void;
  // report
  setDrawer: (k: string | null) => void;
  toggleCohort: () => void;
  copyPrompt: () => void;
  // templates
  loadTemplates: () => void;
  regenerateTemplates: () => void;
  toggleShowAllRoles: () => void;
  useTemplate: (prompt: string) => void; // load into Practice and go there
  copyTemplate: (text: string) => void;
  saveLibraryTemplate: (t: TemplateView) => void; // "save a copy" of a library template
  deleteTemplate: (id: string) => void;
  openBuilder: (seed?: { title: string; fields: RctoFields }) => void;
  closeBuilder: () => void;
  setBuilderTitle: (t: string) => void;
  setBuilderField: (k: keyof RctoFields, v: string) => void;
  saveBuilder: () => void;
  // admin / organisations
  toggleSuppressed: () => void;
  toggleIdentified: () => void;
  openOrg: (id: string) => void; // select an org and open its detail screen
  // campaign create/edit
  openCampaignModal: (mode: 'create' | 'edit', seed?: Partial<CampaignDraft>) => void;
  closeCampaignModal: () => void;
  setCampaignField: (k: keyof CampaignDraft, v: string) => void;
  saveCampaign: () => void;
}

export interface CampaignDraft {
  mode: 'create' | 'edit';
  campaignId?: string;
  name: string;
  sector: string;
  status: 'active' | 'closed' | 'draft' | 'archived';
  periodStart: string;
  periodEnd: string;
  budgetUsd: string;
}

const EMPTY_CAMPAIGN_DRAFT: Omit<CampaignDraft, 'mode'> = { name: '', sector: '', status: 'draft', periodStart: '', periodEnd: '', budgetUsd: '' };

const COMMAND_TEXT: Record<string, string> = {
  skip: 'Skip',
  park: 'Park for later',
  example: 'Give me an example',
  rephrase: 'Rephrase',
  go_deeper: 'Go deeper',
  status: 'Status',
};

export const useStore = create<State>((set, get) => ({
  screen: 'welcome',
  asmtStage: 'consent',
  mode: prospectiveMode(),
  consentChecked: true,
  messages: [],
  composer: '',
  typing: false,
  listening: false,
  voiceError: null,
  topicsDone: 3,
  completed: false,
  role: null,
  ...freshInteractive(),
  drillStep: 0,
  drillIndex: 0,
  passedDrills: [],
  scenario: DEFAULT_SCENARIO,
  practiceFeedback: null,
  completions: [],
  practiceScoring: false,
  practiceError: null,
  draft: '',
  drillScore: null,
  masteryDone: 0,
  drawer: null,
  revealCohort: false,
  copied: false,
  libraryTemplates: [],
  userTemplates: [],
  approvedTools: [],
  templatesRoleLabel: '',
  templatesLoading: false,
  templatesError: null,
  showAllRoles: false,
  builderOpen: false,
  builderTitle: '',
  builderFields: EMPTY_RCTO,
  suppressed: true,
  identified: true, // roster shows participant names by default (admin-only, audit-logged)
  selectedOrg: null,
  campaignModal: null,
  campaignSaving: false,
  campaignError: null,
  extraOrgs: [],
  productName: 'FluentAI',
  noticeCopy: DEFAULT_NOTICE,
  consentVersion: 0,
  needsAuth: authConfigured && !isAuthed(),
  userName: currentUser()?.name ?? currentUser()?.email,
  assessmentId: null,
  liveError: null,
  report: null,
  welcomeLoading: false,
  showSample: false,
  cohort: null,
  ws: null,
  resumable: null,
  history: null,
  historyLoading: false,

  setScreen: (s) => {
    window.scrollTo({ top: 0 });
    if (s === 'practice') {
      // Start each practice session at the first drill with clean working state.
      set({
        screen: s,
        showSample: false,
        drillIndex: 0,
        passedDrills: [],
        drillStep: 0,
        draft: '',
        drillScore: null,
        practiceFeedback: null,
        practiceError: null,
        scenario: DEFAULT_SCENARIO,
      });
      return;
    }
    // Any explicit navigation clears the sample-preview override, so the Report tab shows the
    // user's own report (or the empty state), never the fixture — only viewSample() opts in.
    set({ screen: s, showSample: false });
  },
  viewSample: () => {
    window.scrollTo({ top: 0 });
    set({ screen: 'report', showSample: true });
  },
  setAsmtStage: (asmtStage) => set({ asmtStage }),
  setConsent: (consentChecked) => set({ consentChecked }),

  // Apply branding + product name from the platform settings. Sample mode keeps the built-in
  // brand. Colours are written as CSS custom properties on :root for surfaces wired to them;
  // the product name re-titles the document and the nav. (Full Tailwind token theming and
  // deploy-time no-flash injection are a follow-up — see docs/design-runtime-settings.md.)
  loadPublicSettings: () => {
    if (isSampleMode) return;
    void (async () => {
      try {
        const { settings } = await api.getPublicSettings();
        set({
          productName: settings.branding.productName,
          noticeCopy: settings.branding.noticeCopy?.trim() || DEFAULT_NOTICE,
          consentVersion: settings.brandingVersion,
        });
        if (typeof document !== 'undefined') {
          document.title = settings.branding.productName;
          const root = document.documentElement;
          root.style.setProperty('--brand-primary', settings.branding.colours.primary);
          root.style.setProperty('--brand-accent', settings.branding.colours.accent);
          root.style.setProperty('--brand-code', settings.branding.colours.code);
        }
      } catch {
        // best-effort: the built-in FluentAI brand is the fallback.
      }
    })();
  },

  signIn: () => {
    void login();
  },

  // The store is created at import time, before main.tsx finishes the OAuth callback, so the
  // initial userName/needsAuth can be stale. main.tsx calls this once auth has resolved.
  syncAuth: () =>
    set({ userName: currentUser()?.name ?? currentUser()?.email, needsAuth: authConfigured && !isAuthed() }),

  // Single entry for the Assessment tab and the Welcome "Start" button. Resumes the
  // one active assessment if there is one; otherwise begins a fresh one at consent.
  openAssessment: () => {
    const st = get();
    st.setScreen('assessment');
    if (st.assessmentId && st.asmtStage === 'live') return; // already running this load
    if (liveMode() && st.resumable) {
      get().resumeAssessment(st.resumable.assessmentId);
      return;
    }
    set({ asmtStage: 'consent' }); // beginSetup still resumes if the backend reports an active one
  },

  // Role gate before setup: tailor needs a role. Use the IdP claim if present; otherwise ask.
  // Sample mode skips it (no backend to tailor with — the default set is used).
  beginAssessment: () => {
    if (!liveMode()) {
      get().beginSetup();
      return;
    }
    const known = currentRole();
    if (known) {
      set({ role: known });
      get().beginSetup();
    } else {
      set({ asmtStage: 'role' });
    }
  },

  setRole: (role) => set({ role }),

  // Create the session, then branch on its mode while the "setting up" screen shows:
  //  - quick: role-tailor the static 6-step set (client-side + finalize, no per-turn chat).
  //  - conversational: skip tailoring (it's native to the interview); connect WS if enabled
  //    and fire Avery's opening turn. The mode is assigned by the backend at create time
  //    (env-flagged, off by default) and read back off the session.
  beginSetup: () => {
    set({ asmtStage: 'setup', liveError: null, messages: [], topicsDone: 0, completed: false, ...freshInteractive() });

    if (!liveMode()) {
      const mode = prospectiveMode();
      set({ mode });
      window.setTimeout(() => {
        set({ asmtStage: 'live' });
        if (mode === 'conversational') get().openConversation(); // scripted greeting in sample mode
      }, 2600);
      return;
    }

    void (async () => {
      try {
        const role = get().role ?? undefined;
        // The participant accepted the collection notice at the consent gate; record which version.
        const { session, resumed } = await api.createSession({ role, consent: { version: get().consentVersion } });
        if (resumed) {
          get().resumeAssessment(session.assessmentId);
          return;
        }
        const mode = session.mode ?? 'quick';
        if (mode === 'conversational') {
          set({ assessmentId: session.assessmentId, mode, asmtStage: 'live', completed: false });
          if (wsEnabled) {
            try {
              const ws = new AveryWs(session.assessmentId);
              await ws.connect();
              set({ ws });
            } catch {
              // WS unavailable — REST turns still work with assessmentId set.
            }
          }
          get().openConversation();
          return;
        }
        // quick mode: tailor when we have a role; any failure (or no role) → the default set.
        let tailoredSteps: Step[] | null = null;
        if (role) {
          try {
            const { pack } = await api.tailor(session.assessmentId, role);
            if (pack) tailoredSteps = mergeTailored(pack);
          } catch (err) {
            // Fall back to the default set, but surface why — a failing tailor call should be
            // visible in the browser, not silently swallowed (S1-1).
            console.warn('tailor failed — using the default assessment set', err);
          }
        }
        set({ assessmentId: session.assessmentId, mode, asmtStage: 'live', completed: false, tailoredSteps });
      } catch (err) {
        set({ asmtStage: 'live', liveError: (err as Error).message });
      }
    })();
  },

  // On boot (live mode only), look for an in-progress assessment to offer to continue.
  // In-memory state is lost on logout/refresh, but the backend persists the session.
  checkResumable: () => {
    if (!liveMode()) return;
    void (async () => {
      try {
        const { sessions } = await api.listSessions();
        const inProgress = sessions
          .filter((s) => s.status === 'in_progress' || s.status === 'created')
          .sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''));
        if (inProgress[0]) set({ resumable: inProgress[0] });
      } catch {
        // listing is best-effort; never block the welcome screen on it.
      }
    })();
  },

  // Rehydrate a persisted session: pull its transcript, drop the user back into the
  // live conversation where they left off (WS reconnects for streaming if enabled).
  resumeAssessment: (id) => {
    void (async () => {
      try {
        const { session, messages } = await api.getSession(id);
        const restored: ChatMessage[] = messages.map((m) => ({
          id: m.ts,
          role: m.role,
          type: m.type,
          text: m.text,
        }));
        set({
          assessmentId: id,
          mode: session.mode ?? 'quick', // resume into the right surface (chat vs walkthrough)
          messages: restored,
          screen: 'assessment',
          asmtStage: 'live',
          resumable: null,
          liveError: null,
          topicsDone: Math.max(0, session.stage - 1),
          completed: false,
          ...freshInteractive(),
        });
        if (wsEnabled) {
          try {
            const ws = new AveryWs(id);
            await ws.connect();
            set({ ws });
          } catch {
            // WS unavailable — REST turns still work with assessmentId set.
          }
        }
      } catch (err) {
        set({ resumable: null, liveError: (err as Error).message });
      }
    })();
  },

  // The Welcome dashboard renders the participant's latest completed assessment. On boot
  // (live mode) load that report into state so the hero/radar/roadmap have data; sample
  // mode falls back to the sample report directly in the surface.
  loadWelcome: () => {
    if (!liveMode()) return;
    if (get().report) return; // already loaded this session (e.g. just completed)
    // Signal the fetch so the Dashboard shows the loading veil rather than briefly rendering
    // the get-started hero before the report arrives (the "old page then flip" flash).
    set({ welcomeLoading: true });
    void (async () => {
      try {
        const { sessions } = await api.listSessions();
        const latest = sessions
          .filter((s) => s.status === 'completed')
          .sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''))[0];
        if (!latest) {
          set({ welcomeLoading: false });
          return;
        }
        const { report, cohort } = await api.getReport(latest.assessmentId);
        set({ report, cohort: cohort ?? null, welcomeLoading: false });
        get().loadCompletions();
      } catch {
        // best-effort: Welcome shows the get-started state when no report loads.
        set({ welcomeLoading: false });
      }
    })();
  },

  dismissResumable: () => set({ resumable: null }),

  // Archive an assessment (server-side) so a fresh one can start. Pass an explicit `id` to
  // archive a specific session (e.g. an in-progress row on the dashboard); otherwise the
  // active/resumable one is used. andRestart drops the user back at consent to begin again;
  // otherwise returns to Welcome.
  archiveAssessment: (andRestart, id) => {
    const target = id ?? get().assessmentId ?? get().resumable?.assessmentId ?? null;
    const finish = () => {
      get().ws?.close();
      set({ assessmentId: null, ws: null, messages: [], resumable: null, topicsDone: 0, completed: false, ...freshInteractive() });
      if (andRestart) {
        set({ asmtStage: 'consent', consentChecked: false });
        get().setScreen('assessment');
      } else {
        get().setScreen('welcome');
      }
    };
    if (!liveMode() || !target) {
      finish();
      return;
    }
    void (async () => {
      try {
        await api.archiveSession(target);
      } catch (err) {
        set({ liveError: (err as Error).message });
      }
      finish();
    })();
  },

  // The signed-in user's assessment history (most recent first). Sample mode shows a
  // small scripted list so the screen is demonstrable without a backend.
  loadHistory: () => {
    if (!liveMode()) {
      set({ history: sampleHistory, historyLoading: false });
      return;
    }
    set({ historyLoading: true });
    void (async () => {
      try {
        const { sessions } = await api.listSessions();
        const sorted = [...sessions].sort((a, b) =>
          (b.startedAt ?? '').localeCompare(a.startedAt ?? ''),
        );
        set({ history: sorted, historyLoading: false });
      } catch (err) {
        set({ history: [], historyLoading: false, liveError: (err as Error).message });
      }
    })();
  },

  // Open a completed assessment's report. Sample mode falls back to the sample report.
  openReport: (id) => {
    if (!liveMode()) {
      set({ report: null });
      get().setScreen('report');
      return;
    }
    void (async () => {
      try {
        const { report, cohort } = await api.getReport(id);
        set({ report, cohort: cohort ?? null });
        get().setScreen('report');
        get().loadCompletions();
      } catch (err) {
        set({ liveError: (err as Error).message });
      }
    })();
  },

  setComposer: (composer) => set({ composer }),

  // ---- Interactive assessment (6-step walkthrough) ----
  // Commit the current step: apply skill gains (clamped to 5), record the rendered answer,
  // bump the insight counter, reveal. `extra` carries a scenario's chosen option id so the
  // answer renders against it in the same update.
  // (commit is inlined per-action below to keep the action surface flat for the store type.)
  selectChoice: (id) =>
    set((st) => {
      if (st.revealed) return {};
      const step = (st.tailoredSteps ?? STEPS)[st.stepIdx];
      const skills = { ...st.skills };
      for (const [k, d] of Object.entries(step.gains)) skills[k as keyof Skills] = Math.min(5, skills[k as keyof Skills] + (d as number));
      const answer = renderAnswer(step, { choice: id, promptSel: st.promptSel, rankOrder: st.rankOrder, sliderVal: st.sliderVal, openText: st.openText });
      return { choice: id, revealed: true, insightsCount: st.insightsCount + 1, skills, answers: [...st.answers, answer] };
    }),

  lockIn: () =>
    set((st) => {
      if (st.revealed) return {};
      const step = (st.tailoredSteps ?? STEPS)[st.stepIdx];
      const skills = { ...st.skills };
      for (const [k, d] of Object.entries(step.gains)) skills[k as keyof Skills] = Math.min(5, skills[k as keyof Skills] + (d as number));
      const answer = renderAnswer(step, { choice: st.choice, promptSel: st.promptSel, rankOrder: st.rankOrder, sliderVal: st.sliderVal, openText: st.openText });
      return { revealed: true, insightsCount: st.insightsCount + 1, skills, answers: [...st.answers, answer] };
    }),

  toggleClause: (key) =>
    set((st) => (st.revealed ? {} : { promptSel: { ...st.promptSel, [key]: !st.promptSel[key] } })),

  moveRank: (idx, dir) =>
    set((st) => {
      if (st.revealed) return {};
      const step = (st.tailoredSteps ?? STEPS)[st.stepIdx];
      if (step.kind !== 'rank') return {};
      const arr = (st.rankOrder ?? step.items).slice();
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return {};
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { rankOrder: arr };
    }),

  setSlider: (v) => set((st) => (st.revealed ? {} : { sliderVal: v })),
  setOpenText: (t) => set((st) => (st.revealed ? {} : { openText: t })),

  // Advance to the next topic, or finish: submit the answers to Avery for real scoring and
  // open the report. In sample mode (no backend) just route to the sample report.
  nextStep: () => {
    const st = get();
    const steps = st.tailoredSteps ?? STEPS;
    if (st.stepIdx >= steps.length - 1) {
      if (liveMode() && st.assessmentId) {
        const id = st.assessmentId;
        const answers = st.answers;
        void (async () => {
          try {
            await api.finalize(id, answers);
          } catch {
            // openReport surfaces a "not ready" notice if scoring/report isn't available.
          }
          get().openReport(id);
        })();
      } else {
        get().setScreen('report');
      }
      return;
    }
    set((s) => ({ stepIdx: s.stepIdx + 1, revealed: false, choice: null, promptSel: {}, rankOrder: null, sliderVal: 50, openText: '' }));
  },

  // Stream one turn over the WebSocket, appending tokens to a single Avery bubble.
  streamTurn: (text) => {
    const ws = get().ws;
    if (!ws) {
      set({ typing: false, liveError: 'not connected' });
      return;
    }
    const msgId = nextId();
    ws.sendTurn(text, {
      onStart: () =>
        set((st) => ({ typing: false, messages: [...st.messages, { id: msgId, role: 'avery', type: 'question', text: '' }] })),
      onToken: (chunk) =>
        set((st) => ({ messages: st.messages.map((m) => (m.id === msgId ? { ...m, text: m.text + chunk } : m)) })),
      onComplete: (info) => {
        set((st) => ({ typing: false, topicsDone: st.topicsDone + (info.stageAdvanced ? 1 : 0) }));
        if (info.done) {
          set((st) => ({
            completed: true,
            messages: [...st.messages, { id: nextId(), role: 'avery', type: 'text', text: 'That’s the assessment complete — thank you. Your report is ready whenever you are — open it below.' }],
          }));
          // Pre-load the report so "View your report" is instant, but leave the participant in
          // the conversation. Switching screens out from under them mid-answer is jarring — let
          // them review the transcript and open the report on their own action (Chat composer CTA).
          void (async () => {
            try {
              const { report, cohort } = await api.getReport(get().assessmentId!);
              set({ report, cohort: cohort ?? null });
            } catch {
              set({ liveError: 'Report is not ready yet — try the Report tab in a moment.' });
            }
          })();
        }
      },
      onError: (msg) =>
        set((st) => ({
          typing: false,
          liveError: msg,
          messages: [...st.messages, { id: nextId(), role: 'avery', type: 'text', text: 'Sorry — I had trouble responding just then. Please try again.' }],
        })),
    });
  },

  // Conversational mode: open the interview by firing one turn with EMPTY text — the backend
  // convention for "Avery greets, no user turn". Appends only Avery's reply (no user bubble).
  // sendMessage early-returns on empty text, so this is a dedicated entry point. Greets once.
  openConversation: () => {
    if (get().messages.length > 0 || get().typing) return;
    set({ typing: true });

    if (liveMode() && get().assessmentId) {
      if (wsEnabled && get().ws) {
        get().streamTurn(''); // empty turn → greeting; onStart/onToken render the bubble
        return;
      }
      void (async () => {
        try {
          const r = await api.postMessage(get().assessmentId!, '');
          set((st) => ({
            messages: [...st.messages, { id: nextId(), role: 'avery', type: 'question', text: r.reply.text }],
            typing: false,
            topicsDone: st.topicsDone + (r.stageAdvanced ? 1 : 0),
          }));
        } catch (err) {
          set((st) => ({
            typing: false,
            liveError: (err as Error).message,
            messages: [...st.messages, { id: nextId(), role: 'avery', type: 'text', text: 'Sorry — I had trouble starting just then. Please try again.' }],
          }));
        }
      })();
      return;
    }

    // Sample build (no backend): a scripted greeting so the chat is demonstrable. Uses the
    // dedicated opening line — NOT replies[0] — so the first answer gets a fresh follow-up
    // instead of the greeting repeated back at them.
    window.setTimeout(() => {
      set((st) => ({
        messages: [...st.messages, { id: nextId(), role: 'avery', type: 'question', text: sampleOpening.text }],
        typing: false,
      }));
    }, 900);
  },

  sendMessage: (text) => {
    const content = (text ?? get().composer).trim();
    if (!content) return;
    // Ignore a send while a turn is already in flight (double Enter, Enter+click): a second
    // turn would duplicate the user bubble and race two replies / orphan the WS stream.
    if (get().typing) return;
    // Once complete, the session is closed server-side; a send would 409 and surface as a
    // confusing "trouble responding" error. The composer is locked, but guard here too.
    if (get().completed) return;
    discardDictation();
    const userMsg: ChatMessage = { id: nextId(), role: 'user', type: 'text', text: content };
    set((st) => ({ messages: [...st.messages, userMsg], composer: '', typing: true, listening: false }));

    if (liveMode() && get().assessmentId) {
      // stream over WS when connected; otherwise the REST turn below.
      if (wsEnabled && get().ws) {
        get().streamTurn(content);
        return;
      }
      void (async () => {
        try {
          const r = await api.postMessage(get().assessmentId!, content);
          set((st) => ({
            messages: [...st.messages, { id: nextId(), role: 'avery', type: 'question', text: r.reply.text }],
            typing: false,
            topicsDone: st.topicsDone + (r.stageAdvanced ? 1 : 0),
          }));
          if (r.done) {
            set((st) => ({
              completed: true,
              messages: [
                ...st.messages,
                { id: nextId(), role: 'avery', type: 'text', text: 'That’s the assessment complete — thank you. Your report is ready whenever you are — open it below.' },
              ],
            }));
            // Pre-load the report so "View your report" is instant, but leave the participant in
            // the conversation rather than switching the screen out from under them (see streamTurn).
            try {
              const { report, cohort } = await api.getReport(get().assessmentId!);
              set({ report, cohort: cohort ?? null });
            } catch {
              set({ liveError: 'Report is not ready yet — try the Report tab in a moment.' });
            }
          }
        } catch (err) {
          // AUTH_EXPIRED triggers a guarded reload in the api client; surface others.
          set((st) => ({
            typing: false,
            liveError: (err as Error).message,
            messages: [...st.messages, { id: nextId(), role: 'avery', type: 'text', text: 'Sorry — I had trouble responding just then. Please try again.' }],
          }));
        }
      })();
      return;
    }

    // Not a live session (sample build, or live build before sign-in): scripted reply.
    // Unconditional so there is never a dead state that leaves the typing indicator stuck.
    // The script is a fixed demo exchange, not a cycle: once its replies are spent, close the
    // demo out (completion message + report CTA) rather than wrapping around and re-asking
    // the same questions forever.
    {
      const idx = get().messages.filter((m) => m.role === 'user').length - 1;
      const scripted = idx < sampleAveryReplies.length;
      window.setTimeout(() => {
        if (!scripted) {
          set((st) => ({
            completed: true,
            typing: false,
            messages: [
              ...st.messages,
              { id: nextId(), role: 'avery', type: 'text', text: 'That’s the assessment complete — thank you. Your report is ready whenever you are — open it below.' },
            ],
          }));
          return;
        }
        const reply: ChatMessage =
          idx === 2
            ? {
                id: nextId(),
                role: 'avery',
                type: 'reflection',
                text: 'You use AI daily for drafting and call prep, and you already check figures against the source.',
                insight: 'Your instinct to verify is strong — the opportunity is calibrating where that care goes.',
              }
            : { id: nextId(), role: 'avery', type: 'question', text: sampleAveryReplies[idx] };
        set((st) => ({ messages: [...st.messages, reply], typing: false, topicsDone: st.topicsDone + 1 }));
      }, 1100);
    }
  },

  quickCommand: (cmd) => {
    if (cmd === 'status') {
      const status: ChatMessage = {
        id: nextId(),
        role: 'avery',
        type: 'text',
        text: "You're in stage 1, about 8 minutes in, with a few topics covered. You're tracking well — keep going.",
      };
      set((st) => ({ messages: [...st.messages, status] }));
      return;
    }
    get().sendMessage(COMMAND_TEXT[cmd] ?? cmd);
  },

  // Real voice-to-text via the browser Web Speech API. Dictation is appended to
  // whatever is already typed; interim results stream in live.
  toggleListening: () => {
    if (get().listening) {
      recognition?.stop();
      set({ listening: false });
      return;
    }
    const Ctor = speechCtor();
    if (!Ctor) {
      set({ voiceError: 'Voice input isn’t supported in this browser — please type instead.' });
      return;
    }
    discardDictation();
    const rec = new Ctor();
    recognition = rec;
    rec.lang = 'en-AU';
    rec.continuous = true;
    rec.interimResults = true;
    const base = get().composer.trim();
    rec.onresult = (e) => {
      if (recognition !== rec) return;
      let spoken = '';
      for (let i = 0; i < e.results.length; i++) spoken += e.results[i][0].transcript;
      set({ composer: [base, spoken.trim()].filter(Boolean).join(base ? ' ' : '') });
    };
    rec.onerror = (e) => {
      if (recognition !== rec) return;
      set({
        listening: false,
        voiceError:
          e.error === 'not-allowed' || e.error === 'service-not-allowed'
            ? 'Microphone permission denied — check your browser settings.'
            : null,
      });
      recognition = null;
    };
    rec.onend = () => {
      if (recognition !== rec) return;
      set({ listening: false });
      recognition = null;
    };
    try {
      rec.start();
      set({ listening: true, voiceError: null });
    } catch {
      discardDictation();
      set({ listening: false });
    }
  },

  resolveAssumption: (id, resolution) =>
    set((st) => ({
      messages: st.messages.map((m) => (m.id === id ? { ...m, resolved: resolution } : m)),
    })),

  setDrillStep: (drillStep) => set({ drillStep }),
  setDraft: (draft) => set({ draft }),
  // Reset the drill for a new scenario (the Transfer step's "apply to your own task").
  startDrill: (scenario, gotoDraft = true) =>
    set({
      scenario: scenario.trim() || DEFAULT_SCENARIO,
      draft: '',
      drillScore: null,
      practiceFeedback: null,
      practiceError: null,
      drillStep: gotoDraft ? 2 : 1,
    }),

  // Advance to the next drill in the sequence (clamped at the last) and reset working state.
  nextDrill: () =>
    set((st) => {
      const count = st.report?.practiceDrills?.length || BUILTIN_DRILLS.length;
      return {
        drillIndex: Math.min(st.drillIndex + 1, count - 1),
        drillStep: 0,
        draft: '',
        drillScore: null,
        practiceFeedback: null,
        practiceError: null,
      };
    }),

  // Load the user's saved practice completions so the report/Practice can show what's done.
  loadCompletions: () => {
    if (!liveMode()) return;
    void (async () => {
      try {
        const { completions } = await api.listPracticeCompletions();
        set({ completions });
      } catch (err) {
        console.warn('failed to load practice completions', err);
      }
    })();
  },

  // Tick off a drill / the 48-hour action, with an optional reflection. Optimistic; persists
  // in live mode (in sample mode it stays in-memory so the UI is still demonstrable).
  markComplete: (itemType, itemRef, reflection) => {
    const assessmentId = get().report?.assessmentId;
    const completion: PracticeCompletion = {
      itemType,
      itemRef,
      assessmentId,
      done: true,
      reflection: reflection?.trim() || undefined,
      completedAt: new Date().toISOString(),
    };
    set((st) => ({
      completions: [...st.completions.filter((c) => !(c.itemType === itemType && c.itemRef === itemRef)), completion],
    }));
    if (!liveMode()) return;
    void api
      .savePracticeCompletion({ itemType, itemRef, reflection: completion.reflection, assessmentId })
      .catch((err) => console.warn('failed to save practice completion', err));
  },

  // Score the drafted prompt. Live: the LLM coach (POST /practice/score). Sample: canned.
  submitDraft: () => {
    const draft = get().draft.trim();
    if (!draft) return;
    if (!liveMode()) {
      set((st) => ({
        drillStep: 3,
        drillScore: samplePractice.score,
        practiceFeedback: samplePractice,
        practiceError: null,
        masteryDone: samplePractice.score >= 8 ? Math.min(3, st.masteryDone + 1) : st.masteryDone,
        passedDrills:
          samplePractice.score >= 8 && !st.passedDrills.includes(st.drillIndex)
            ? [...st.passedDrills, st.drillIndex]
            : st.passedDrills,
      }));
      return;
    }
    set({ practiceScoring: true, practiceError: null });
    void (async () => {
      try {
        const { score } = await api.scorePractice({ draft, scenario: get().scenario });
        set((st) => ({
          practiceScoring: false,
          drillStep: 3,
          drillScore: score.score,
          practiceFeedback: score,
          masteryDone: score.score >= 8 ? Math.min(3, st.masteryDone + 1) : st.masteryDone,
          passedDrills:
            score.score >= 8 && !st.passedDrills.includes(st.drillIndex)
              ? [...st.passedDrills, st.drillIndex]
              : st.passedDrills,
        }));
      } catch (err) {
        set({ practiceScoring: false, practiceError: (err as Error).message });
      }
    })();
  },

  setDrawer: (drawer) => set({ drawer }),
  toggleCohort: () => set((st) => ({ revealCohort: !st.revealCohort })),
  copyPrompt: () => {
    set({ copied: true });
    window.setTimeout(() => set({ copied: false }), 1800);
  },

  // ---- Templates ----
  loadTemplates: () => {
    if (!liveMode()) {
      set({ libraryTemplates: sampleTemplates, approvedTools: sampleApprovedTools, templatesRoleLabel: 'Presales Consultant', templatesError: null });
      return;
    }
    set({ templatesLoading: true, templatesError: null });
    void (async () => {
      try {
        const r = await api.listTemplates();
        set({ templatesLoading: false, libraryTemplates: r.library, userTemplates: r.userTemplates, approvedTools: r.approvedTools, templatesRoleLabel: r.roleLabel });
      } catch (err) {
        set({ templatesLoading: false, templatesError: (err as Error).message });
      }
    })();
  },
  regenerateTemplates: () => {
    if (!liveMode()) return; // sample set is fixed
    set({ templatesLoading: true, templatesError: null });
    void (async () => {
      try {
        const r = await api.listTemplates({ regenerate: true });
        set({ templatesLoading: false, libraryTemplates: r.library, approvedTools: r.approvedTools, templatesRoleLabel: r.roleLabel });
      } catch (err) {
        set({ templatesLoading: false, templatesError: (err as Error).message });
      }
    })();
  },
  toggleShowAllRoles: () => set((st) => ({ showAllRoles: !st.showAllRoles })),
  useTemplate: (prompt) => {
    get().startDrill(prompt);
    set({ screen: 'practice' });
  },
  copyTemplate: (text) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    set({ copied: true });
    window.setTimeout(() => set({ copied: false }), 1800);
  },
  saveLibraryTemplate: (t) => {
    const optimistic: UserTemplateView = {
      templateId: `local-${t.templateId}`,
      title: t.title,
      fields: t.fields,
      prompt: t.prompt,
      fromTemplateId: t.templateId,
      createdAt: new Date().toISOString(),
    };
    if (!liveMode()) {
      set((st) => ({ userTemplates: [optimistic, ...st.userTemplates] }));
      return;
    }
    void (async () => {
      try {
        const { template } = await api.saveTemplate({ title: t.title, fields: t.fields, fromTemplateId: t.templateId });
        set((st) => ({ userTemplates: [template, ...st.userTemplates] }));
      } catch (err) {
        set({ templatesError: (err as Error).message });
      }
    })();
  },
  deleteTemplate: (id) => {
    set((st) => ({ userTemplates: st.userTemplates.filter((t) => t.templateId !== id) }));
    if (!liveMode()) return;
    void api.deleteTemplate(id).catch((err) => set({ templatesError: (err as Error).message }));
  },
  openBuilder: (seed) =>
    set({ builderOpen: true, builderTitle: seed?.title ?? '', builderFields: seed?.fields ?? EMPTY_RCTO }),
  closeBuilder: () => set({ builderOpen: false, builderTitle: '', builderFields: EMPTY_RCTO }),
  setBuilderTitle: (builderTitle) => set({ builderTitle }),
  setBuilderField: (k, v) => set((st) => ({ builderFields: { ...st.builderFields, [k]: v } })),
  saveBuilder: () => {
    const { builderTitle, builderFields } = get();
    const fields = builderFields;
    const title = builderTitle.trim();
    if (!title || !fields.role.trim() || !fields.context.trim() || !fields.task.trim() || !fields.outputFormat.trim()) {
      set({ templatesError: 'Add a title and all four parts (Role, Context, Task, Output format).' });
      return;
    }
    const prompt = [`Role: ${fields.role}`, `Context: ${fields.context}`, `Task: ${fields.task}`, `Output format: ${fields.outputFormat}`].join('\n\n');
    if (!liveMode()) {
      const local: UserTemplateView = { templateId: `local-${Date.now()}`, title, fields, prompt, createdAt: new Date().toISOString() };
      set((st) => ({ userTemplates: [local, ...st.userTemplates], builderOpen: false, builderTitle: '', builderFields: EMPTY_RCTO, templatesError: null }));
      return;
    }
    void (async () => {
      try {
        const { template } = await api.saveTemplate({ title, fields });
        set((st) => ({ userTemplates: [template, ...st.userTemplates], builderOpen: false, builderTitle: '', builderFields: EMPTY_RCTO, templatesError: null }));
      } catch (err) {
        set({ templatesError: (err as Error).message });
      }
    })();
  },

  toggleSuppressed: () => set((st) => ({ suppressed: !st.suppressed })),
  toggleIdentified: () => set((st) => ({ identified: !st.identified })),
  openOrg: (id) => {
    window.scrollTo({ top: 0 });
    set({ selectedOrg: id, screen: 'orgDetail' });
  },

  openCampaignModal: (mode, seed) =>
    set({ campaignModal: { mode, ...EMPTY_CAMPAIGN_DRAFT, ...seed }, campaignError: null }),
  closeCampaignModal: () => set({ campaignModal: null, campaignError: null, campaignSaving: false }),
  setCampaignField: (k, v) =>
    set((st) => (st.campaignModal ? { campaignModal: { ...st.campaignModal, [k]: v }, campaignError: null } : {})),

  // Create or edit an organisation. Live mode calls the API; sample mode persists in-memory
  // (extraOrgs) so the new/edited org appears in the list without a backend.
  saveCampaign: () => {
    const d = get().campaignModal;
    if (!d) return;
    if (!d.name.trim()) { set({ campaignError: 'Name is required.' }); return; }
    const body = {
      name: d.name.trim(),
      sector: d.sector.trim() || undefined,
      status: d.status,
      periodStart: d.periodStart || undefined,
      periodEnd: d.periodEnd || undefined,
      budgetUsd: d.budgetUsd === '' ? undefined : Number(d.budgetUsd),
    };

    if (!liveMode()) {
      // Sample mode: reflect the change in the in-memory list so the UI is demonstrable.
      const id = d.campaignId ?? d.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const summary: OrgSummary = {
        campaignId: id, name: body.name, sector: body.sector, internal: false, status: d.status,
        participantCount: 0, completeCount: 0, completionPct: 0,
        avgReadiness: { band: 'developing', label: 'Developing' },
        spentUsd: 0, budgetUsd: body.budgetUsd ?? null,
      };
      set((st) => ({
        extraOrgs: d.mode === 'create'
          ? [summary, ...st.extraOrgs.filter((o) => o.campaignId !== id)]
          : st.extraOrgs.map((o) => (o.campaignId === id ? { ...o, ...summary } : o)),
        campaignModal: null,
      }));
      return;
    }

    set({ campaignSaving: true, campaignError: null });
    void (async () => {
      try {
        if (d.mode === 'create') await api.createCampaign(body);
        else if (d.campaignId) await api.updateCampaign(d.campaignId, body);
        set({ campaignSaving: false, campaignModal: null });
      } catch (err) {
        set({ campaignSaving: false, campaignError: (err as Error).message });
      }
    })();
  },
}));
